import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, LoadingController, AlertController } from '@ionic/angular/lazy';
import { OrganisationService } from 'src/app/services/organisation.service';
import { OrganisationPickerPage } from 'src/app/modals/organisation-picker/organisation-picker.page';
import { ObserverService } from 'src/app/services/observer.service';
import { PricingTierService } from 'src/app/services/pricing-tier.service';
import { SupportedCurrencyService } from 'src/app/services/supported-currency.service';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'app-quote-filter',
    templateUrl: './quote-filter.page.html',
    styleUrls: ['./quote-filter.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class QuoteFilterPage implements OnInit {
    @Input() archived;
    @Input() currency_id;
    @Input() organisation_id;
    @Input() pricing_tier_id;
    @Input() status;

    spinner = null;
    loading_spinner = null;
    loading = false;
    online = true;
    filter_params = {};
    pricing_tiers = [];
    supported_currencies = null;
    organisation = null;
    organisation_title = null;
    modal_css_class = '';

    status_options: any = { header: 'Status' };
    currency_options: any = { header: 'Currency' };
    pricing_tier_options: any = { header: 'Pricing tier' };
    archived_options: any = { header: 'Archived' };

    // modal flags
    organisation_picker_modal_open = false;

    constructor(
        public modalController: ModalController,
        public loadingController: LoadingController,
        public alertController: AlertController,
        private organisationService: OrganisationService,
        private observerService: ObserverService,
        private pricingTierService: PricingTierService,
        private supportedCurrencyService: SupportedCurrencyService,
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.modal_css_class = 'fullscreen';
        }

        this.loadData().then(async () => {

            if(this.status) {
                this.filter_params['status'] = this.status;
            }

            if(this.currency_id) {
                this.filter_params['currency_id'] = this.currency_id;
            }

            if(this.pricing_tier_id) {
                this.filter_params['pricing_tier_id'] = this.pricing_tier_id;
            }

            if(this.organisation_id) {
                this.filter_params['organisation_id'] = this.organisation_id;
                this.organisationService.getOrganisation(this.filter_params['organisation_id'],{}).then((organisation) => {
                    if(organisation) {
                        this.organisation_title = organisation["title"];
                    }
                })
                .catch(() => { });
            }

            if(this.archived != undefined) {
                this.filter_params['archived'] = this.archived;
            }

        })
        .catch(() => { });

        this.observerService.subscribe('network_status_changed|quote_filter_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => { }).catch(() => { });
                }
                else {
                    this.presentAlert("Offline", "You must be online to filter quotes, please check your network connection and try again.");
                }
            }
        });

    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading currencies").then(() => {
                this.loadSupportedCurrencies().then(() => {
                    this.loading_spinner.message = "Loading pricing tiers";
                    this.loadPricingTiers().then(() => {
                        this.loading_spinner.dismiss();
                        resolve();
                    })
                    .catch(() => {
                        this.loading_spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    ngOnDestroy() {
        this.observerService.destroy("network_status_changed|quote_filter_modal");
    }

    loadPricingTiers() {
        return new Promise<void>((resolve, reject) => {
            this.pricing_tiers = [];
            this.pricingTierService.getPricingTiers().then((pricing_tiers: any) => {
                if(pricing_tiers) {
                    pricing_tiers.sort((a, b) => {
                        if(a['order'] < b['order']) { return -1; }
                        if(a['order'] > b['order']) { return 1; }
                        return 0;
                    });
                    this.pricing_tiers = pricing_tiers;
                    let pricing_tier_found = false
                    for(let i = 0; i < this.pricing_tiers.length; i++) {
                        if(this.pricing_tiers[i].id == this.filter_params['pricing_tier_id']) {
                            pricing_tier_found = true;
                            break
                        }
                    }
                    if(!pricing_tier_found) {
                        delete this.filter_params['pricing_tier_id'];
                    }
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
    }

    loadSupportedCurrencies() {
        return new Promise<void>((resolve, reject) => {
            this.supportedCurrencyService.getSupportedCurrencies().then((supported_currencies: any) => {
                if(supported_currencies) {
                    supported_currencies.sort((a, b) => {
                        if (a['title'].toLowerCase() < b['title'].toLowerCase()) { return -1; }
                        if (a['title'].toLowerCase() > b['title'].toLowerCase()) { return 1; }
                        return 0;
                    });
                    this.supported_currencies = supported_currencies;
                    let currency_found = false
                    for(let i = 0; i < this.supported_currencies.length; i++) {
                        if(this.supported_currencies[i].id == this.filter_params['currency_id']) {
                            currency_found = true;
                            break
                        }
                    }
                    if(!currency_found) {
                        delete this.filter_params['currency_id'];
                    }
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        })
    }

    close() {
        this.modalController.dismiss();
    }

    filter() {
        this.modalController.dismiss(this.filter_params);
    }

    clearStatus() {
        delete this.filter_params['status'];
    }

    clearCurrency() {
        delete this.filter_params['currency_id'];
    }

    clearPricingTier() {
        delete this.filter_params['pricing_tier_id'];
    }

    clearArchived() {
        delete this.filter_params['archived'];
    }

    clearFilters() {
        this.presentSpinner("Clearing filters").then(() => {


            if(this.filter_params['status']) {
                delete this.filter_params["status"];
            }
            if(this.filter_params['currency_id']) {
                delete this.filter_params["currency_id"];
            }
            if(this.filter_params['pricing_tier_id']) {
                delete this.filter_params["pricing_tier_id"];
            }
            if(this.filter_params['organisation_id']) {
                delete this.filter_params["organisation_id"];
                this.organisation_title = null;
            }
            if('archived' in this.filter_params) {
                delete this.filter_params["archived"];
            }

            this.spinner.dismiss();

        });
    }

    async presentOrganisationPickerModal() {
        if(!this.organisation_picker_modal_open) {
            this.organisation_picker_modal_open = true;
            const modal = await this.modalController.create({
                component: OrganisationPickerPage,
                cssClass: this.modal_css_class,
                componentProps: {
                    organisation_id: (this.filter_params['organisation_id'] ? this.filter_params['organisation_id'] : null),
                    hide_add_organisation_button: true
                }
            });
            modal.onDidDismiss().then((data) => {
                this.organisation_picker_modal_open = false;
                if(data.data) {
                    if(data.data['picker_action'] == "organisation_deleted") {
                        delete this.filter_params["organisation_id"];
                        this.organisation_title = null;
                    }
                    else {
                        this.filter_params['organisation_id'] = data.data['organisation_id'];
                        this.organisation_title = data.data['organisation_title'];
                    }

                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            cssClass:'alert',
            buttons: ['OK']
        });
        await alert.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

}